import { useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { useThemeMode } from '_metronic/partials'
import { extractAreaChart } from 'Utils/ApexChartsTooltipsExtractration'
import formatNumberCommas from 'Utils/formatNumberCommas'
import { useDashboardStore } from 'Store/Dashboard'
import { shallow } from 'zustand/shallow'
import moment from 'moment'

const ViewModel = () => {
	const chartRef = useRef<HTMLDivElement | null>(null)
	const { mode } = useThemeMode()
	const reportData = useDashboardStore(state => state.data, shallow)

	const getChartOptions = (): ApexOptions => {
		const oemDatas = (reportData?.saleMonthlyReport ?? []).map((d: any) => parseFloat(d.oem))
		const marketplaceDatas = (reportData?.saleMonthlyReport ?? []).map((d: any) =>
			parseFloat(d.marketplace)
		)
		const months = (reportData?.saleMonthlyReport ?? []).map((data: any) =>
			moment(data.month, 'YYYY-MM').format('MMM YYYY')
		)

		return {
			subtitle: {
				text: 'ยอดขาย',
				margin: 0,
				style: {
					fontSize: '18px',
					fontWeight: 'bold',
					fontFamily: 'Noto Sans Thai, sans-serif',
				},
			},
			series: [
				{
					name: 'OEM',
					data: oemDatas,
				},
				{
					name: 'Marketplace',
					data: marketplaceDatas,
				},
			],
			chart: {
				height: 350,
				type: 'area',
				zoom: {
					enabled: false,
				},
				toolbar: {
					show: false,
				},
			},
			dataLabels: {
				enabled: false,
			},
			stroke: {
				curve: 'straight',
				lineCap: 'square',
			},
			grid: {
				row: {
					colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
					opacity: 0.5,
				},
				padding: {
					top: 55,
				},
			},
			xaxis: {
				title: {
					text: 'Month',
				},
				categories: months,
			},
			yaxis: {
				title: {
					text: '฿ (THB)',
				},
			},
			legend: {
				show: true,
				position: 'top',
			},
			tooltip: {
				custom: function ({ dataPointIndex, w, seriesIndex }) {
					const datas = w.globals.initialSeries
					const dataColor = w.globals.colors

					const extractedData = extractAreaChart(datas, dataColor, dataPointIndex, seriesIndex)

					return `<div class="fw-medium bg-kumopack-base-black fw-bold p-1 text-kumopack-base-white">฿ ${formatNumberCommas(
						extractedData.value
					)}</div>`
				},
			},
		}
	}

	const refreshChart = () => {
		if (!chartRef.current) {
			return
		}

		const chart = new ApexCharts(chartRef.current, getChartOptions())
		if (chart) {
			chart.render()
		}

		return chart
	}

	useEffect(() => {
		const chart = refreshChart()

		return () => {
			if (chart) {
				chart.destroy()
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chartRef, mode, reportData])

	return { chartRef }
}

export default ViewModel
